import React, { useState, useEffect } from 'react';
import axios from 'axios';
import NewsCard from '../components/NewsCard';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Loader2, TrendingUp, Flame } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Trending = () => {
  const { getAuthHeader } = useAuth();
  const { t } = useLanguage();
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTrending = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/trending`, {
        headers: getAuthHeader()
      });
      setTrending(response.data.trending || []);
    } catch (error) {
      console.error('Error fetching trending:', error);
      toast.error('Failed to load trending news');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrending();
  }, []);

  const handleBookmarkToggle = async (newsId, isBookmarked) => {
    try {
      if (isBookmarked) {
        await axios.delete(`${API}/bookmarks/${newsId}`, {
          headers: getAuthHeader()
        });
        toast.success('Bookmark removed');
      } else {
        await axios.post(`${API}/bookmarks?news_id=${newsId}`, {}, {
          headers: getAuthHeader()
        });
        toast.success('Bookmark added');
      }
      fetchTrending();
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF5] dark:bg-[#1a1816]">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Flame className="w-8 h-8 text-[#FF6B6B]" strokeWidth={2.5} />
            <h1 className="text-5xl md:text-7xl font-bold dark:text-white" style={{ fontFamily: 'Merriweather, serif' }}>
              {t('trending.title')}
            </h1>
            <TrendingUp className="w-8 h-8 text-[#4ECDC4]" strokeWidth={2.5} />
          </div>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {t('trending.subtitle')}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" strokeWidth={2.5} />
          </div>
        ) : trending.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-gray-600 dark:text-gray-400" data-testid="no-trending-message">
              {t('trending.empty')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="trending-grid">
            {trending.map((item, index) => (
              <div key={item.id} className="relative">
                {index < 3 && (
                  <div className="absolute -top-4 -left-4 z-10 bg-[#FF6B6B] text-white font-bold rounded-full w-12 h-12 flex items-center justify-center border-2 border-black dark:border-white text-xl" style={{ fontFamily: 'Merriweather, serif' }}>
                    #{index + 1}
                  </div>
                )}
                <NewsCard
                  news={item}
                  onBookmarkToggle={handleBookmarkToggle}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Trending;